import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Download, ExternalLink, Files, ShieldCheck, Sparkles } from "lucide-react";
import { DoxMascot } from "@/components/Logo";
import api, { API } from "@/lib/api";

/**
 * Dox's Work History — "Everything Dox has done for you."
 *
 * A calm, outcome-first list of every completed piece of work. Cards feel
 * like value, not archive. Pinned above the list is the "This Month Dox
 * Saved You" summary — proof-of-value every time the customer opens the app.
 *
 * Placement:
 *   - Embedded on the Overview page (customer sees it every visit)
 *   - Also available as its own dashboard tab
 *
 * Copy rules (Doug's directive):
 *   - Never uses "Archive", "Vault", "Records", or feature names
 *   - Every title is what Dox DID, not what the customer can operate
 */
export default function WorkHistorySection({ variant = "embedded" }) {
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    let live = true;
    Promise.all([
      api.get("/work-history?limit=100"),
      api.get("/work-history/summary"),
    ])
      .then(([h, s]) => {
        if (!live) return;
        setItems(h.data?.items || []);
        setSummary(s.data || null);
      })
      .catch(() => { if (live) setError("Couldn't load history right now."); });
    return () => { live = false; };
  }, []);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (error) {
    return (
      <div className={variant === "embedded" ? "" : "p-8"} data-testid="work-history">
        <p className="text-white/50 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <section className={variant === "embedded" ? "" : "p-8 max-w-5xl mx-auto"} data-testid="work-history">
      {variant !== "embedded" && (
        <header className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.28em] text-dox-orange font-bold flex items-center gap-1.5">
            <Sparkles size={12} /> {"Dox's Work History"}
          </div>
          <h1 className="mt-2 font-display font-black text-white text-4xl sm:text-5xl tracking-[-0.02em]">
            {"Everything Dox has done for you."}
          </h1>
        </header>
      )}

      {/* Pinned summary — proof-of-value */}
      <SummaryCard summary={summary} />

      {variant === "embedded" && (
        <div className="flex items-baseline justify-between mt-10 mb-5">
          <h2 className="font-display font-black text-white text-2xl sm:text-3xl tracking-[-0.01em]">
            {"Everything Dox has done for you."}
          </h2>
          <span className="text-white/40 text-xs uppercase tracking-[0.22em]">Work history</span>
        </div>
      )}

      {/* List */}
      {items === null ? (
        <SkeletonList />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <ol className="space-y-3">
          {items.map((it) => (
            <WorkCard
              key={it.id + it.kind}
              item={it}
              expanded={expanded.has(it.id + it.kind)}
              onToggle={() => toggle(it.id + it.kind)}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Pinned summary                                                              */
/* -------------------------------------------------------------------------- */

function SummaryCard({ summary }) {
  const s = summary || {};
  const skeleton = !summary;
  return (
    <div
      data-testid="work-history-summary"
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-dox-orange/[0.09] via-white/[0.02] to-white/[0.04] p-6 sm:p-8"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(30,120,255,0.35), transparent 60%)" }}
      />
      <div className="relative">
        <div className="flex items-center gap-3">
          <DoxMascot size={44} emotion="done" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-dox-orange font-bold">
              This {s.month_label ? s.month_label.split(" ")[0] : "month"} Dox saved you
            </div>
            <div className="font-display font-black text-white text-2xl sm:text-3xl leading-tight mt-1">
              {skeleton ? "—" : `${s.hours_saved} hours`}
              <span className="text-white/40 font-medium text-lg sm:text-xl"> of paperwork</span>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-4">
          <SummaryStat label="Renewals completed" value={s.renewals_completed} testid="summary-renewals" />
          <SummaryStat label="Documents organized" value={s.documents_organized} testid="summary-documents" />
          <SummaryStat label="Certifications" value={s.certifications} testid="summary-certifications" />
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 sm:px-5 sm:py-4" data-testid={testid}>
      <div className="font-display font-black text-white text-2xl sm:text-3xl">{value ?? 0}</div>
      <div className="text-white/50 text-[11px] tracking-wide uppercase mt-1">{label}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Card                                                                        */
/* -------------------------------------------------------------------------- */

function WorkCard({ item, expanded, onToggle }) {
  const isPreview = item.mode === "preview";
  const when = formatWhen(item.completed_at);
  const pdfUrl = item.record_id ? `${API}/verify/${item.record_id}/pdf` : null;
  const verifyUrl = item.record_id ? `/verify/${item.record_id}` : null;

  return (
    <li
      data-testid={`work-card-${item.id}`}
      className="rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.035] transition-colors"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-4 text-left px-4 sm:px-5 py-4"
        aria-expanded={expanded}
        data-testid={`work-card-toggle-${item.id}`}
      >
        <span className="h-8 w-8 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0">
          <Check size={14} strokeWidth={3} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-white font-medium text-[15px]">{item.title}</span>
            {isPreview && (
              <span
                className="inline-flex items-center rounded-full border border-amber-400/25 bg-amber-500/[0.08] text-amber-300 px-2 py-[1.5px] text-[10px] tracking-wide"
                data-testid="work-card-rehearsal-pill"
              >
                Rehearsal
              </span>
            )}
          </div>
          <div className="text-white/50 text-sm mt-0.5 flex flex-wrap items-center gap-x-1.5">
            <span className="text-white/60">Completed by Dox</span>
            <span className="text-white/25">·</span>
            <span>{when}</span>
            {item.authority && (
              <>
                <span className="text-white/25">·</span>
                <span className="text-white/45">{item.authority}</span>
              </>
            )}
          </div>
        </div>
        <ChevronDown
          size={16}
          className={`text-white/40 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.33, 1, 0.68, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-white/5 px-4 sm:px-5 py-5 space-y-5">
              {/* What Dox did */}
              {item.trace?.length > 0 && (
                <DetailBlock icon={<Check size={13} strokeWidth={3} />} label="What Dox did">
                  <ol className="space-y-2">
                    {item.trace.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                        <span className="text-emerald-400 mt-0.5"><Check size={11} strokeWidth={3} /></span>
                        <span>
                          <span className="font-medium">{s.headline || s.label}</span>
                          {s.detail && <span className="text-white/50"> — {s.detail}</span>}
                        </span>
                      </li>
                    ))}
                  </ol>
                </DetailBlock>
              )}

              {/* Why it was required */}
              {(item.jurisdiction || item.authority) && (
                <DetailBlock icon={<Files size={13} />} label="Why it was required">
                  <p className="text-white/70 text-sm leading-relaxed">
                    {item.classification ? <span className="text-white">{item.classification}</span> : "This task"}
                    {item.jurisdiction ? <> is regulated in <span className="text-white">{item.jurisdiction}</span></> : null}
                    {item.authority ? <> by <span className="text-white">{item.authority}</span></> : null}.
                    Dox handled it on your behalf.
                  </p>
                </DetailBlock>
              )}

              {/* Documents used */}
              {item.source_doc_id && (
                <DetailBlock icon={<Files size={13} />} label="Documents used">
                  <p className="text-white/60 text-sm">
                    Based on the document Dox has on file for you.
                  </p>
                </DetailBlock>
              )}

              {/* Verification source */}
              {item.authority && (
                <DetailBlock icon={<ShieldCheck size={13} />} label="Verification source">
                  <div className="flex flex-wrap items-center gap-2 text-white/70 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-emerald-300">
                      <ShieldCheck size={12} strokeWidth={2.4} /> Dox verified this
                    </span>
                    <span className="text-white/30">·</span>
                    <span className="text-white">{item.authority}</span>
                    {item.jurisdiction && (
                      <span className="inline-flex items-center rounded-full border border-emerald-400/25 bg-emerald-500/[0.08] text-emerald-300 px-2 py-[1px] text-[10px]">
                        {item.jurisdiction}
                      </span>
                    )}
                  </div>
                </DetailBlock>
              )}

              {/* Receipt + QR + verify */}
              {item.record_id && (
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  {pdfUrl && (
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 px-4 py-2 text-sm text-white/85 transition"
                      data-testid={`work-card-pdf-${item.id}`}
                    >
                      <Download size={13} /> Save receipt
                    </a>
                  )}
                  {verifyUrl && (
                    <a
                      href={verifyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-dox-orange/15 hover:bg-dox-orange/25 text-dox-orange px-4 py-2 text-sm font-medium transition"
                      data-testid={`work-card-verify-${item.id}`}
                    >
                      See what Dox completed <ExternalLink size={12} />
                    </a>
                  )}
                  <QrThumb url={verifyUrl && (typeof window !== "undefined" ? window.location.origin + verifyUrl : verifyUrl)} />
                </div>
              )}

              <div className="pt-1 text-[10px] text-white/25 tracking-wider uppercase">
                {item.record_id ? <>Record {item.record_id}</> : null}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

function DetailBlock({ icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold mb-2">
        {icon} <span>{label}</span>
      </div>
      {children}
    </div>
  );
}

/**
 * Tiny inline QR (SVG). We generate a client-side canvas via a public QR
 * server URL would leak — instead we render an <img> using the same public
 * verify link. Since /api/verify/{id}/pdf embeds the QR too, the customer
 * always has a scannable copy on the printable receipt.
 */
function QrThumb({ url }) {
  if (!url) return null;
  // Use Google Charts fallback-style QR via qrserver.com is a 3rd-party call;
  // to keep this UI dependency-free, we simply render a small "QR" badge that
  // links to the same verify URL. The tamper-evident QR lives in the PDF.
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 px-3.5 py-2 text-[11px] text-white/55 transition"
      title="Public verification link"
      data-testid="work-card-qr-link"
    >
      <span className="grid grid-cols-3 gap-[1.5px]" aria-hidden="true">
        {[1,0,1,0,1,0,1,0,1].map((v, i) => (
          <span key={i} className={`w-[3px] h-[3px] rounded-[0.5px] ${v ? "bg-white/70" : "bg-transparent"}`} />
        ))}
      </span>
      Public link
    </a>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center" data-testid="work-history-empty">
      <div className="mx-auto mb-4"><DoxMascot size={56} emotion="idle" /></div>
      <div className="font-display font-black text-white text-xl">
        {"Dox hasn't finished anything yet — but I'm on it."}
      </div>
      <p className="mt-2 text-white/50 text-sm max-w-md mx-auto">
        {"Upload your first license and I'll get started right away."}
      </p>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-16 rounded-2xl border border-white/5 bg-white/[0.02] animate-pulse" />
      ))}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "recently";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "recently";
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "recently";
  }
}
